<template>
  <div v-if="priceCatalog" class="grid h-full grid-cols-1 grid-rows-[auto_1fr]">
    <!-- Header -->
    <BaseDetailsPreviewEditHeader
      :is-horizontal-layout="isHorizontalLayout"
      :title="modifiedPriceCatalog?.name"
      link-name="price-catalogs"
      :is-modified="isModified"
      :mode="mode"
      :is-edit-active="priceCatalog.isFromManager"
      @edit-mode="onEditMode"
      @cancel="onCancelEdit"
      @save="onSaveChanges"
      @horizontal-layout="(value: boolean) => (isHorizontalLayout = value)" />

    <AppResizableContainer
      :layout="isHorizontalLayout ? 'vertical' : 'horizontal'"
      :minSize="300"
      class="overflow-auto">
      <template #headerContent>
        <!-- Horizontal Layout -->
        <template v-if="isHorizontalLayout">
          <PriceCatalogDetails
            v-if="modifiedPriceCatalog"
            v-model="modifiedPriceCatalog"
            :priceCatalog="priceCatalog"
            :mode="mode"
            :isHorizontalLayout="isHorizontalLayout">
          </PriceCatalogDetails>
        </template>

        <!-- Vertical Layout -->
        <template v-else>
          <PerfectScrollbar class="w-full h-full py-4 space-y-2" ref="scrollbarId">
            <PriceCatalogDetails
              v-if="modifiedPriceCatalog"
              v-model="modifiedPriceCatalog"
              :priceCatalog="priceCatalog"
              :mode="mode"
              :isHorizontalLayout="isHorizontalLayout">
            </PriceCatalogDetails>
          </PerfectScrollbar>
        </template>
      </template>

      <!-- Tables Section -->
      <template #detailsTitle>
        <div
          class="px-4 py-1.5 text-sm text-gray-500 capitalize border-b-2 w-fit border-theme-500 font-medium">
          {{ $t('material.materials') }}
        </div>
      </template>

      <template #detailsContent>
        <PriceCatalogDetailsTable
          :table-data="priceCatalogDetailsTableData"
          :all-modified-data="modifiedPriceCatalogDetails"
          :table-data-count="priceCatalogDetailsTableDataTotalCount"
          :selected-page="priceCatalogDetailsSelectedPage"
          :mode="mode"
          :header-include-vat="modifiedPriceCatalog ? modifiedPriceCatalog.includeVat : false"
          :price-catalog-id="String(route.params.id)"
          :addedDetails="addedModifiedDetails"
          @refresh-table-data="loadPriceCatalogDetails"
          @add-all="onAddAllMaterials"
          @add-materials="onAddMaterials"
          @delete-materials="onDeletePriceCatalogDetails"
          @delete-all="onDeleteAllPriceCatalogDetails"
          @material-info="onMaterialInfo"
          @update-materials="onUpdateMaterials"></PriceCatalogDetailsTable>
      </template>
    </AppResizableContainer>

    <BaseDrawerConfirm
      :is-active="confirmModalActive"
      @cancel="confirmModalActive = false"
      @confirm="onDiscardChanges" />
    <PriceCatalogMaterialInfo
      v-if="selectedMaterial"
      :material="selectedMaterial"
      :open="showMaterialInfoModal"
      :material-types="dropdownMaterialTypes"
      @close="
        {
          selectedMaterial = null;
          showMaterialInfoModal = false;
        }
      "></PriceCatalogMaterialInfo>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watchEffect } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import useVuelidate from '@vuelidate/core';
import { useI18n } from 'vue-i18n';
import { useToastStore } from '@/stores/toast';
import { deepCopy } from '@/utils/data';
import {
  getPriceCatalogById,
  addOrEditPriceCatalog,
  getPriceCatalogDetailsWithConditions,
  addOrEditPriceCatalogDetails,
  addPriceCatalogDetailsAllMaterials,
  removePriceCatalogDetailsByPriceCatalogId,
} from '@/services/api/prices';
import { getMaterialById } from '@/services/api/materials';
import AppResizableContainer from '@/components/common/AppResizableContainer.vue';
import BaseDetailsPreviewEditHeader from '@/components/common/BaseDetailsPreviewEditHeader.vue';
import BaseDrawerConfirm from '@/components/common/BaseDrawerConfirm.vue';
import PriceCatalogDetails from '@/components/prices/PriceCatalogDetails.vue';
import PriceCatalogDetailsTable from '@/components/prices/PriceCatalogDetailsTable.vue';
import PriceCatalogMaterialInfo from '@/components/prices/PriceCatalogMaterialInfo.vue';

import type { PerfectScrollbarExpose } from 'vue3-perfect-scrollbar';
import type { PriceCatalog, PriceCatalogDetail } from '@/types/PricingTypes';
import { RecordMode, type TableFilterData } from '@/types/Base';
import type { Material } from '@/types/Materials';
import { useLookupsStore } from '@/stores/lookups';
import { storeToRefs } from 'pinia';

const $v = useVuelidate();
const route = useRoute();
const router = useRouter();
const { t: $t } = useI18n();
const toastStore = useToastStore();
const priceCatalog = ref<PriceCatalog>();
const modifiedPriceCatalog = ref<PriceCatalog>();
const modifiedPriceCatalogDetails = ref<PriceCatalogDetail[]>([]);
const priceCatalogDetails = ref<PriceCatalogDetail[]>([]);

const addedModifiedDetails = computed(() =>
  modifiedPriceCatalogDetails.value.filter((x) => x.recordMode === RecordMode.Added)
);

const isHorizontalLayout = ref(true);
const mode = ref<'view' | 'edit'>('view');
const { getMaterialTypesLookup } = useLookupsStore();

onMounted(async () => {
  await loadData();
  await getMaterialTypesLookup();
  if (priceCatalog.value) modifiedPriceCatalog.value = deepCopy<PriceCatalog>(priceCatalog.value);
  await loadPriceCatalogDetails({
    page: 1,
    itemsPerPage: priceCatalogDetailsItemsPerPage.value,
    priceCatalogId: String(route.params.id),
  });
});
const { dropdownMaterialTypes } = storeToRefs(useLookupsStore());

async function loadData() {
  const res = await getPriceCatalogById(route.params.id as string);
  if (res) {
    priceCatalog.value = res as PriceCatalog;
    mode.value = 'view';
  } else {
    (priceCatalog.value = {
      id: route.params.id as string,
      isFromManager: true,
      isActive: true,
      name: '',
      code: '',
      startDate: initDate(),
      endDate: initDate(),
      includeVat: false,
      companyId: '',
    }),
      (mode.value = 'edit');
  }
}
function initDate() {
  const today = new Date();
  today.setUTCHours(0, 0, 0, 0);
  return today.toISOString().split('.')[0];
}

const isModified = ref<boolean>(false);

watchEffect(() => {
  isModified.value =
    JSON.stringify(modifiedPriceCatalog.value) !== JSON.stringify(priceCatalog.value) ||
    JSON.stringify(modifiedPriceCatalogDetails.value) !== JSON.stringify(priceCatalogDetails.value);
});

const confirmModalActive = ref(false);
const isBackMode = ref(false);

function onCancelEdit(backMode: string) {
  if (isModified.value) {
    confirmModalActive.value = true;
    if (backMode == 'back') {
      isBackMode.value = true;
    }
    return;
  } else if (!priceCatalog.value?.createdDate) {
    router.push('/price-catalogs');
  }
  mode.value = 'view';
}

async function onDiscardChanges() {
  mode.value = 'view';
  confirmModalActive.value = false;
  if (priceCatalog.value) modifiedPriceCatalog.value = deepCopy<PriceCatalog>(priceCatalog.value);
  modifiedPriceCatalogDetails.value = deepCopy<PriceCatalogDetail[]>(priceCatalogDetails.value);
  if (!priceCatalog.value?.createdDate || isBackMode.value) {
    router.push('/price-catalogs');
  }
  await loadPriceCatalogDetails({
    page: 1,
    itemsPerPage: priceCatalogDetailsItemsPerPage.value,
    priceCatalogId: String(route.params.id),
  });
  priceCatalogDetailsSelectedPage.value = 1;
}

async function onSaveChanges() {
  $v.value.$touch();
  if ($v.value.$invalid) return;
  if (!isModified.value) return;
  mode.value = 'view';
  if (JSON.stringify(modifiedPriceCatalog.value) !== JSON.stringify(priceCatalog.value)) {
    await addOrEditPriceCatalog(modifiedPriceCatalog.value as PriceCatalog);
  }
  await addOrEditPriceCatalogDetails(modifiedPriceCatalogDetails.value);
  modifiedPriceCatalogDetails.value = [];

  await loadData();
  await loadPriceCatalogDetails({
    itemsPerPage: priceCatalogDetailsItemsPerPage.value,
    priceCatalogId: String(route.params.id),
  });
  priceCatalogDetailsSelectedPage.value = 1;
  isModified.value = false;
}

const scrollbarId = ref<PerfectScrollbarExpose>();

function onEditMode() {
  mode.value = 'edit';
}

function onDeletePriceCatalogDetails(details: PriceCatalogDetail[]) {
  details.forEach((detail) => {
    const existingIndex = modifiedPriceCatalogDetails.value.findIndex((x) => x.id === detail.id);
    if (existingIndex !== -1) {
      const existingDetail = modifiedPriceCatalogDetails.value[existingIndex];
      if (existingDetail.recordMode === RecordMode.Added) {
        modifiedPriceCatalogDetails.value.splice(existingIndex, 1);
        const existingIndexInTable = priceCatalogDetailsTableData.value.findIndex(
          (x) => x.id === detail.id
        );
        if (existingIndexInTable !== -1)
          priceCatalogDetailsTableData.value.splice(existingIndexInTable, 1);
      } else if (existingDetail.recordMode === RecordMode.Modified) {
        existingDetail.recordMode = RecordMode.Deleted;
        const existingIndexInTable = priceCatalogDetailsTableData.value.find(
          (x) => x.id === detail.id
        );
        if (existingIndexInTable) existingIndexInTable.deleted = true;
      }
    } else {
      const existing = priceCatalogDetailsTableData.value.find((x) => x.id === detail.id);
      if (existing) existing.deleted = true;
      detail.recordMode = RecordMode.Deleted;
      modifiedPriceCatalogDetails.value.push(detail);
    }
  });
}

async function onDeleteAllPriceCatalogDetails(priceCatalogId: string) {
  const result = await removePriceCatalogDetailsByPriceCatalogId(priceCatalogId);
  if (result) {
    toastStore.success({ text: $t('messages.success.deletedSuccessfully') });
    modifiedPriceCatalogDetails.value = [];
    await loadPriceCatalogDetails({ priceCatalogId, page: 1, itemsPerPage: 10 });
    priceCatalogDetailsSelectedPage.value = 1;
    isModified.value = false;
    mode.value = 'view';
  } else {
    toastStore.error({ text: $t('messages.error.cannotDelete') });
  }
}

async function onAddAllMaterials(
  price: number,
  allowDiscounts: boolean,
  tableQuery: string | undefined
) {
  $v.value.$touch();
  if ($v.value.$invalid) return;
  if (!modifiedPriceCatalog.value?.id) return;
  mode.value = 'view';
  if (JSON.stringify(modifiedPriceCatalog.value) !== JSON.stringify(priceCatalog.value)) {
    await addOrEditPriceCatalog(modifiedPriceCatalog.value as PriceCatalog);
  }
  await addPriceCatalogDetailsAllMaterials(
    modifiedPriceCatalog.value.id,
    modifiedPriceCatalog.value?.includeVat,
    price,
    allowDiscounts,
    tableQuery
  );
  await loadPriceCatalogDetails({
    page: 1,
    itemsPerPage: priceCatalogDetailsItemsPerPage.value,
    priceCatalogId: String(route.params.id),
  });
}
const showMaterialInfoModal = ref(false);

const selectedMaterial = ref<Material | null>(null);

async function onMaterialInfo(id: string) {
  showMaterialInfoModal.value = true;
  selectedMaterial.value = await getMaterialById(id);
}

const priceCatalogDetailsTableData = ref<PriceCatalogDetail[]>([]);
const priceCatalogDetailsTableDataTotalCount = ref<number>(0);
const priceCatalogDetailsSelectedPage = ref(1);
const priceCatalogDetailsItemsPerPage = ref(10);

async function loadPriceCatalogDetails(data: TableFilterData & { priceCatalogId: string }) {
  if (!modifiedPriceCatalog.value) return;
  const currentPage = data.page ?? 1;
  priceCatalogDetailsItemsPerPage.value = data.itemsPerPage ?? 10;

  let addedDetails: PriceCatalogDetail[] = [];
  if (modifiedPriceCatalogDetails.value) {
    addedDetails = modifiedPriceCatalogDetails.value.filter(
      (x) => x.recordMode == RecordMode.Added
    );
  }

  let serverDetails: PriceCatalogDetail[] = [];
  let serverDetailsCount: number = 0;
  const res = await getPriceCatalogDetailsWithConditions({ ...data });
  if (res) {
    serverDetails = res.data;
    serverDetailsCount = res.count;
  }
  const serverPages = Math.ceil(serverDetailsCount / priceCatalogDetailsItemsPerPage.value);
  const serverPageItemCount = serverDetails.length;
  const pageAvailableSpace = Math.max(
    0,
    priceCatalogDetailsItemsPerPage.value - serverPageItemCount
  );

  if (currentPage < serverPages) {
    // get page fromServer
    priceCatalogDetailsTableData.value = serverDetails;
  } else if (currentPage > serverPages) {
    // get page from local data
    const mod = serverDetailsCount % priceCatalogDetailsItemsPerPage.value;
    const availableSpace = mod === 0 ? 0 : priceCatalogDetailsItemsPerPage.value - mod;
    const modifiedCurrentPage = currentPage - serverPages;
    const startIndex =
      (modifiedCurrentPage - 1) * priceCatalogDetailsItemsPerPage.value + availableSpace;
    const endIndex = startIndex + priceCatalogDetailsItemsPerPage.value;
    priceCatalogDetailsTableData.value = addedDetails.slice(startIndex, endIndex);
  } else {
    // get boundary page (last server page + fill with modified)
    if (pageAvailableSpace > 0) {
      const modifiedData = addedDetails.slice(0, pageAvailableSpace);
      priceCatalogDetailsTableData.value = [...serverDetails, ...modifiedData];
    } else {
      priceCatalogDetailsTableData.value = serverDetails;
    }
  }

  priceCatalogDetailsTableDataTotalCount.value = serverDetailsCount + addedDetails.length;

  // Filter deleted from TableData
  let deletedDetails: PriceCatalogDetail[] = [];
  if (modifiedPriceCatalogDetails.value) {
    deletedDetails = modifiedPriceCatalogDetails.value.filter(
      (x) => x.recordMode == RecordMode.Deleted
    );
  }
  if (deletedDetails.length) {
    deletedDetails.forEach((deleted) => {
      const existing = priceCatalogDetailsTableData.value.find((x) => x.id === deleted.id);
      if (existing) existing.deleted = true;
    });
  }

  // Update detail that have modified values
  let updatedDetails: PriceCatalogDetail[] = [];
  if (modifiedPriceCatalogDetails.value) {
    updatedDetails = modifiedPriceCatalogDetails.value.filter(
      (x) => x.recordMode == RecordMode.Modified
    );
  }
  if (updatedDetails.length) {
    updatedDetails.forEach((detail) => {
      const existingIndex = priceCatalogDetailsTableData.value.findIndex((x) => x.id === detail.id);
      if (existingIndex !== -1) {
        priceCatalogDetailsTableData.value[existingIndex] = detail;
      }
    });
  }
}

async function onAddMaterials(details: PriceCatalogDetail[]) {
  if (!modifiedPriceCatalogDetails.value) modifiedPriceCatalogDetails.value = [];
  modifiedPriceCatalogDetails.value.push(...details);

  // This will trigger load table data from refreshing table to last page
  const lastPage = Math.ceil(
    priceCatalogDetailsTableDataTotalCount.value / priceCatalogDetailsItemsPerPage.value
  );
  if (priceCatalogDetailsSelectedPage.value === lastPage)
    await loadPriceCatalogDetails({
      page: lastPage,
      itemsPerPage: priceCatalogDetailsItemsPerPage.value,
      priceCatalogId: String(route.params.id),
    });
  else priceCatalogDetailsSelectedPage.value = lastPage;
}

function onUpdateMaterials(detail: PriceCatalogDetail) {
  const previousDetail = priceCatalogDetailsTableData.value.find((x) => x.id == detail.id);
  if (previousDetail && previousDetail.price !== 0) detail.previousPrice = previousDetail.price;

  detail.isFromManager = true;

  const existingIndex = modifiedPriceCatalogDetails.value.findIndex((x) => x.id === detail.id);
  if (existingIndex !== -1) {
    modifiedPriceCatalogDetails.value.splice(existingIndex, 1);
  }
  modifiedPriceCatalogDetails.value.push(detail);

  const existingInTable = priceCatalogDetailsTableData.value.findIndex((x) => x.id === detail.id);
  if (existingInTable !== -1) {
    priceCatalogDetailsTableData.value[existingInTable] = detail;
  }
}
</script>
